import { OTPVerificationScreen } from '@/components/organisms/OTPVerificationScreen';
import { logger } from '@/core/logger';
import { REGISTRATION_STEP, useManualRegistrationStore } from '@/modules/onboarding/registration/store/registrationStore';
import { useRegistrationProgress } from '@/modules/onboarding/registration/utils/registrationProgress';
import { useOTP } from '@/modules/onboarding/verification/otp/hooks/useOTP';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';

export default function PhoneOTPInline() {
    const params = useLocalSearchParams();
    const router = useRouter();

    // Use central progress calculation instead of URL params
    const progress = useRegistrationProgress();
    const initialContactInfo = params.contactInfo as string | undefined;

    const [contactInfo, setContactInfo] = useState<string>(initialContactInfo || '');
    const [showOTP, setShowOTP] = useState<boolean>(!!initialContactInfo);

    // Get registration store
    const registrationStore = useManualRegistrationStore();
    const customerId = registrationStore.registrationResponse?.customer_id || null;
    const registrationId = registrationStore.registrationResponse?.registration_id || null;
    const setPhoneOtpVerified = registrationStore.setPhoneOtpVerified;
    const setPhoneOtpTransactionId = registrationStore.setPhoneOtpTransactionId;
    const setStep = registrationStore.setStep;

    // Check if this is a registration flow (formData exists but registrationResponse is null)
    const isRegistrationFlow = !!registrationStore.formData && !registrationStore.registrationResponse;

    const {
        transactionId,
        isLoading: isVerifyingOTP,
        error: otpError,
        isVerified,
        verifyOtp,
        resendOtp,
        clearError,
        sendOtp,
        resetVerification,
    } = useOTP({
        type: 'phone',
        contactInfo,
        autoSend: false, // We'll send manually when contact is submitted
        isRegistration: isRegistrationFlow, // Use registration OTP endpoint
    });

    const [isSendingOTP, setIsSendingOTP] = useState(false);

    const handleContactSubmit = useCallback((contact: string) => {
        logger.info('PhoneOTPInline', 'Phone submitted, sending OTP', { contact, isRegistrationFlow });
        setContactInfo(contact);
        
        // For registration flow, update step to PHONE_OTP immediately when phone is submitted
        // This prevents splash screen from re-routing on app restart
        if (isRegistrationFlow) {
            setStep(REGISTRATION_STEP.PHONE_OTP);
            logger.info('PhoneOTPInline', 'Step updated to PHONE_OTP for registration flow');
        }
        
        // Show OTP input immediately (don't wait for API response)
        setShowOTP(true);
        setIsSendingOTP(true);
        // Send OTP in background (don't await - let it run async)
        sendOtp(contact)
            .then(() => {
                logger.info('PhoneOTPInline', 'OTP sent successfully');
            })
            .catch((error) => {
                logger.error('PhoneOTPInline', 'Failed to send OTP', error);
            })
            .finally(() => {
                setIsSendingOTP(false);
            });
    }, [sendOtp, isRegistrationFlow, setStep]);

    const handleOTPComplete = useCallback(async (otp: string) => {
        logger.info('PhoneOTPInline', 'Phone OTP entered, verifying...', { otp, isRegistrationFlow });
        const verified = await verifyOtp(otp);
        
        if (verified && isRegistrationFlow) {
            // For registration flow, handle success directly (no verify-details API)
            logger.info('PhoneOTPInline', 'Phone OTP verified successfully (registration flow)');
            
            // Extract country code from phone number
            const parsePhoneNumber = (phone: string): { countryCode: string; mobileNumber: string } => {
                if (!phone) {
                    return { countryCode: '971', mobileNumber: '' };
                }
                const cleaned = phone.replace(/\s+/g, '').trim();
                if (cleaned.startsWith('+')) {
                    const match = cleaned.match(/^\+(\d{1,3})(.+)$/);
                    if (match && match[1] && match[2]) {
                        return {
                            countryCode: match[1]!,
                            mobileNumber: match[2]!,
                        };
                    }
                }
                const knownCodes = ['971', '1', '44', '33', '49', '39', '34', '86', '81', '82', '91', '61', '55', '52', '7', '27'];
                for (const code of knownCodes) {
                    if (cleaned.startsWith(code)) {
                        return {
                            countryCode: code,
                            mobileNumber: cleaned.slice(code.length),
                        };
                    }
                }
                return {
                    countryCode: '971',
                    mobileNumber: cleaned.replace(/^\+?971/, ''),
                };
            };
            
            const { countryCode, mobileNumber } = parsePhoneNumber(contactInfo);
            
            // Store phone OTP verification status
            setPhoneOtpVerified(true);
            // Update step to email-otp
            setStep(REGISTRATION_STEP.EMAIL_OTP);
            // Save verified phone number (mobile number only, without country code) and country code separately
            // Register API expects phone_number to be just the mobile number, country_code is separate
            registrationStore.setVerifiedContact('phone', mobileNumber, countryCode);
            
            // Store phone OTP transaction ID in state
            if (transactionId) {
                setPhoneOtpTransactionId(transactionId);
                logger.info('PhoneOTPInline', 'Phone OTP transaction ID stored', { transactionId });
            } else {
                logger.warn('PhoneOTPInline', 'Phone OTP verified but transaction ID is missing');
            }
            
            // Navigate to email OTP
            router.push('/email-otp-inline' as any);
        }
        
        return verified; // Return verification result for OTPVerificationScreen to handle verify-details (non-registration flow)
    }, [verifyOtp, isRegistrationFlow, contactInfo, setPhoneOtpVerified, setPhoneOtpTransactionId, setStep, registrationStore, router, transactionId]);

    const handleVerifyDetailsSuccess = useCallback((_type: 'phone' | 'email', contact: string) => {
        // This is only called for non-registration flows (when customerId and registrationId exist)
        logger.info('PhoneOTPInline', 'Verify-details API succeeded, storing phone OTP verification status');
        // Store phone OTP verification status in registration store
        setPhoneOtpVerified(true);
        // Save verified phone number to registration store
        registrationStore.setVerifiedContact('phone', contact);
        router.push('/email-otp-inline' as any);
    }, [router, setPhoneOtpVerified, registrationStore]);

    const handleResendOTP = useCallback(async () => {
        logger.info('PhoneOTPInline', 'Resend OTP requested');
        clearError();
        await resendOtp();
    }, [resendOtp, clearError]);

    const handleEditContact = useCallback(() => {
        // Reset showOTP when edit is clicked
        setShowOTP(false);
        // Reset OTP verification state (success/failure)
        resetVerification();
    }, [resetVerification]);

    return (
        <OTPVerificationScreen
            type="phone"
            mode="inline"
            progress={progress}
            contactInfo={contactInfo}
            showOTP={showOTP}
            loading={isSendingOTP}
            verifyingOTP={isVerifyingOTP}
            otpError={otpError}
            otpSuccess={isVerified}
            onContactSubmit={handleContactSubmit}
            onEditContact={handleEditContact}
            onOTPComplete={handleOTPComplete}
            onResendOTP={handleResendOTP}
            onOTPChange={clearError}
            {...(customerId && registrationId
                ? {
                    verifyDetailsConfig: {
                        customerId,
                        registrationId,
                    },
                    onVerifyDetailsSuccess: handleVerifyDetailsSuccess,
                }
                : {})}
        />
    );
}
